import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getDataProvider } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import type { ImageLibraryItem, CreateImageLibraryData } from './sceneDisplayConfigs'
import { logger } from '@/lib/logger'
import { processImageForUpload, generatePreviewFromUrl } from '@/lib/imageOptimizer'
import { useImageCacheStore } from './imageCache'

export const useImageLibraryStore = defineStore('imageLibrary', () => {
  const authStore = useAuthStore()
  const images = ref<ImageLibraryItem[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const initializationPromise = ref<Promise<ImageLibraryItem[]> | null>(null)
  
  // Track which images are currently having previews generated
  const previewGenerationInProgress = ref<Set<string>>(new Set())

  /**
   * Get image URL - uses imageCache for optimized fetching
   * Only requests signed URL if not already cached
   * @deprecated Use getImageByPath from imageCache directly for better performance
   */
  const getImageUrl = async (storagePath: string): Promise<string> => {
    const imageCacheStore = useImageCacheStore()
    // Use path-based caching - only fetches signed URL on cache miss
    return await imageCacheStore.getImageByPath(storagePath, 'user-images')
  }

  /**
   * Get image URL synchronously if cached, empty string otherwise
   * Use this for immediate display without blocking
   */
  const getCachedImageUrl = (storagePath: string): string => {
    const imageCacheStore = useImageCacheStore()
    return imageCacheStore.getCachedUrlByPath(storagePath)
  }

  // Fetch all images for the current user
  const fetchImages = async (): Promise<ImageLibraryItem[]> => {
    if (initialized.value) return images.value
    if (initializationPromise.value) return initializationPromise.value
    
    if (!authStore.user?.id) {
      throw new Error('User not authenticated')
    }

    loading.value = true
    error.value = null

    initializationPromise.value = (async () => {
      try {
        const provider = getDataProvider()
        const data = await provider.query<any>('image_library', {
          orderBy: [{ column: 'uploaded_at', ascending: false }]
        })

        // OPTIMIZED: Don't generate signed URLs upfront anymore
        // Instead, store the storage_path and let imageCache handle URL generation on-demand
        const imagesWithPaths = (data || []).map((img: any) => {
          const result: ImageLibraryItem = {
            ...img,
            // Store storage paths - URLs will be fetched on-demand via imageCache
            url: '', // Will be populated on-demand
            preview_url: undefined, // Will be populated on-demand
            // Keep storage paths for on-demand URL generation
            storage_path: img.storage_path,
            preview_storage_path: img.preview_storage_path
          }
          return result
        })

        images.value = imagesWithPaths
        initialized.value = true
        
        // Optionally preload preview images in background (don't await)
        const imageCacheStore = useImageCacheStore()
        const previewPaths = imagesWithPaths
          .filter((img: ImageLibraryItem) => img.preview_storage_path)
          .map((img: ImageLibraryItem) => img.preview_storage_path!)
        
        if (previewPaths.length > 0) {
          logger.info(`[ImageLibrary] Preloading ${previewPaths.length} preview images in background`)
          imageCacheStore.preloadImagesByPath(previewPaths, 'user-images').catch(err => {
            logger.warn('[ImageLibrary] Preview preload failed:', err)
          })
        }
        
        return imagesWithPaths
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to fetch images'
        throw err
      } finally {
        loading.value = false
        initializationPromise.value = null
      }
    })()

    return initializationPromise.value
  }

  // Upload a new image
  const uploadImage = async (file: File, tags: string[] = []): Promise<ImageLibraryItem> => {
    if (!authStore.user?.id) {
      throw new Error('User not authenticated')
    }

    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      // Generate unique filename base
      const fileExt = file.type === 'image/png' ? 'png' : 
                      file.type === 'image/webp' ? 'webp' : 'png'
      const fileBase = `${Date.now()}-${Math.random().toString(36).substring(2)}`
      const fileName = `${fileBase}.${fileExt}`
      const previewFileName = `${fileBase}_preview.${fileExt}`
      const storagePath = `${authStore.user.id}/${fileName}`
      const previewStoragePath = `${authStore.user.id}/${previewFileName}`

      // Process the image: optimize for storage and generate preview
      logger.info(`[ImageLibrary] Processing image: ${file.name} (${file.size} bytes)`)
      const processed = await processImageForUpload(file)
      logger.info(`[ImageLibrary] Processed: full=${processed.width}x${processed.height} (resized=${processed.wasResized}), preview=${processed.previewWidth}x${processed.previewHeight}`)

      // Upload optimized full image
      const uploadResult = await provider.uploadFile('user-images', storagePath, processed.optimizedBlob)
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload file')
      }

      // Upload preview image
      const previewUploadResult = await provider.uploadFile('user-images', previewStoragePath, processed.previewBlob)
      if (!previewUploadResult.success) {
        logger.warn('Failed to upload preview, continuing without it:', previewUploadResult.error)
      }

      // Create database record
      const imageData: CreateImageLibraryData = {
        filename: fileName,
        original_name: file.name,
        file_size: processed.optimizedBlob.size,
        mime_type: file.type === 'image/png' ? 'image/png' : 
                   file.type === 'image/webp' ? 'image/webp' : 'image/png',
        storage_path: storagePath,
        preview_storage_path: previewUploadResult.success ? previewStoragePath : undefined,
        width: processed.width,
        height: processed.height,
        tags
      }

      const result = await provider.insert<any>('image_library', imageData)

      if (!result.success || !result.data) {
        // If database insert fails, clean up uploaded files
        await provider.deleteFile('user-images', storagePath)
        if (previewUploadResult.success) {
          await provider.deleteFile('user-images', previewStoragePath)
        }
        throw new Error(result.error || 'Failed to create image record')
      }

      const newImage: ImageLibraryItem = {
        ...result.data,
        // Don't generate signed URLs upfront - let imageCache handle on-demand
        url: '',
        preview_url: undefined,
        storage_path: result.data.storage_path,
        preview_storage_path: result.data.preview_storage_path
      }

      // Pre-cache the blobs we already have so the image is instantly available
      // in the editor without a round-trip to storage for a signed URL
      const imageCacheStore = useImageCacheStore()
      await imageCacheStore.cacheBlobDirectly(storagePath, processed.optimizedBlob)
      if (previewUploadResult.success) {
        await imageCacheStore.cacheBlobDirectly(previewStoragePath, processed.previewBlob)
      }

      // Add to local state
      images.value.unshift(newImage)

      return newImage
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to upload image'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Upload a video file (no processing, stored as-is)
  const uploadVideo = async (file: File, tags: string[] = []): Promise<ImageLibraryItem> => {
    if (!authStore.user?.id) {
      throw new Error('User not authenticated')
    }

    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      const extMap: Record<string, string> = {
        'video/mp4': 'mp4',
        'video/webm': 'webm',
        'video/quicktime': 'mov',
      }
      const fileExt = extMap[file.type] || 'mp4'
      const fileBase = `${Date.now()}-${Math.random().toString(36).substring(2)}`
      const fileName = `${fileBase}.${fileExt}`
      const storagePath = `${authStore.user.id}/${fileName}`

      logger.info(`[ImageLibrary] Uploading video: ${file.name} (${file.size} bytes)`)

      const uploadResult = await provider.uploadFile('user-images', storagePath, file)
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload video')
      }

      const imageData: CreateImageLibraryData = {
        filename: fileName,
        original_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        storage_path: storagePath,
        preview_storage_path: undefined,
        width: undefined,
        height: undefined,
        tags
      }

      const result = await provider.insert<any>('image_library', imageData)

      if (!result.success || !result.data) {
        await provider.deleteFile('user-images', storagePath)
        throw new Error(result.error || 'Failed to create video record')
      }

      const newItem: ImageLibraryItem = {
        ...result.data,
        url: '',
        preview_url: undefined,
        storage_path: result.data.storage_path,
        preview_storage_path: result.data.preview_storage_path
      }

      images.value.unshift(newItem)
      return newItem
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to upload video'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Delete an image
  const deleteImage = async (imageId: string): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      // Find the image to get storage path
      const image = images.value.find(img => img.id === imageId)
      if (!image) {
        throw new Error('Image not found')
      }

      // Delete from database
      const result = await provider.delete('image_library', imageId)

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete image')
      }

      // Delete main image from storage
      const storageResult = await provider.deleteFile('user-images', image.storage_path)
      if (!storageResult.success) {
        logger.warn('Failed to delete file from storage:', storageResult.error)
      }

      // Delete preview from storage if it exists
      if (image.preview_storage_path) {
        const previewResult = await provider.deleteFile('user-images', image.preview_storage_path)
        if (!previewResult.success) {
          logger.warn('Failed to delete preview from storage:', previewResult.error)
        }
      }

      // Remove from local state
      images.value = images.value.filter(img => img.id !== imageId)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete image'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Generate preview for an existing image that doesn't have one
  const generatePreviewForExisting = async (imageId: string): Promise<void> => {
    // Check if already in progress
    if (previewGenerationInProgress.value.has(imageId)) {
      return
    }

    const image = images.value.find(img => img.id === imageId)
    if (!image || image.preview_storage_path) {
      // Image not found or already has preview
      return
    }

    if (!image.url) {
      logger.warn(`[ImageLibrary] Cannot generate preview for ${imageId}: no URL`)
      return
    }

    previewGenerationInProgress.value.add(imageId)
    
    try {
      const provider = getDataProvider()
      
      // Generate preview from the existing image URL
      logger.info(`[ImageLibrary] Generating preview for existing image: ${image.original_name}`)
      const preview = await generatePreviewFromUrl(image.url, image.mime_type)
      
      // Generate preview filename
      const fileBase = image.filename.replace(/\.[^.]+$/, '')
      const fileExt = image.mime_type === 'image/png' ? 'png' : 
                      image.mime_type === 'image/webp' ? 'webp' : 'png'
      const previewFileName = `${fileBase}_preview.${fileExt}`
      const userId = image.storage_path.split('/')[0]
      const previewStoragePath = `${userId}/${previewFileName}`
      
      // Upload preview
      const uploadResult = await provider.uploadFile('user-images', previewStoragePath, preview.blob)
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload preview')
      }
      
      // Update database record
      const updateResult = await provider.update<any>('image_library', imageId, {
        preview_storage_path: previewStoragePath
      })
      
      if (!updateResult.success) {
        // Clean up uploaded preview
        await provider.deleteFile('user-images', previewStoragePath)
        throw new Error(updateResult.error || 'Failed to update image record')
      }
      
      // Update local state
      const index = images.value.findIndex(img => img.id === imageId)
      if (index !== -1) {
        images.value[index] = {
          ...images.value[index],
          preview_storage_path: previewStoragePath,
          preview_url: await getImageUrl(previewStoragePath)
        }
      }
      
      logger.info(`[ImageLibrary] Preview generated for: ${image.original_name}`)
    } catch (err) {
      logger.error(`[ImageLibrary] Failed to generate preview for ${imageId}:`, err)
    } finally {
      previewGenerationInProgress.value.delete(imageId)
    }
  }

  // Get the best URL for preview display (preview if available, otherwise full)
  const getPreviewUrl = (image: ImageLibraryItem): string => {
    return image.preview_url || image.url || ''
  }

  // Search images by name or tags
  const searchImages = (query: string, selectedTags: string[] = []): ImageLibraryItem[] => {
    const lowercaseQuery = query.toLowerCase()
    
    return images.value.filter(image => {
      // Check if query matches name or filename
      const matchesQuery = !query || 
        image.original_name.toLowerCase().includes(lowercaseQuery) ||
        image.filename.toLowerCase().includes(lowercaseQuery)
      
      // Check if image has any of the selected tags
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.some(tag => image.tags.includes(tag))
      
      return matchesQuery && matchesTags
    })
  }

  // Update image metadata (tags, etc.)
  const updateImage = async (imageId: string, updates: { tags?: string[] }): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const result = await provider.update<any>('image_library', imageId, updates)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update image')
      }

      // Update local state
      const index = images.value.findIndex(img => img.id === imageId)
      if (index !== -1) {
        images.value[index] = {
          ...images.value[index],
          ...result.data,
          url: await getImageUrl(result.data.storage_path)
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update image'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Helper function to get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }
      img.src = URL.createObjectURL(file)
    })
  }

  // Get all unique tags from images
  const getAllTags = (): string[] => {
    const tagSet = new Set<string>()
    images.value.forEach(image => {
      image.tags.forEach(tag => tagSet.add(tag))
    })
    return Array.from(tagSet).sort()
  }

  return {
    // State
    images,
    loading,
    error,

    // Actions
    fetchImages,
    uploadImage,
    uploadVideo,
    deleteImage,
    searchImages,
    updateImage,
    getImageUrl,
    getCachedImageUrl,
    getAllTags,
    generatePreviewForExisting,
    getPreviewUrl
  }
})